(() => {
  'use strict';
  const instances = new Map();
  function destroy(root, instance) {
    instance.abort.abort();
    instance.observer.disconnect();
    instance.cleanupPreview?.();
    instance.unsubscribe?.();
    instance.cancelFrame?.();
    if (instance.dialog?.open) instance.dialog.close();
    instance.modal?.querySelectorAll('.ew-modal-arrow').forEach((button) => button.remove());
    if (instance.modal?.dataset.ewOwner === root.dataset.ewSection) {
      if (instance.modal.hasAttribute('open')) instance.modal.hide?.();
      instance.modal.remove();
    }
    instances.delete(root);
  }
  function init(root) {
    if (!root || instances.has(root)) return;
    for (const [previous, instance] of instances) {
      if (!previous.isConnected || previous.dataset.ewSection === root.dataset.ewSection) destroy(previous, instance);
    }
    const designMode = Boolean(window.Shopify?.designMode);
    const abort = new AbortController();
    const opts = { signal: abort.signal };
    const id = root.dataset.ewSection;
    const gallery = root.querySelector('media-gallery');
    const viewer = root.querySelector('[id^="GalleryViewer-"]');
    const slider = root.querySelector('[id^="Slider-Gallery-"]');
    const dots = root.querySelector('[data-ew-dots]');
    const form = root.querySelector(`form#product-form-${id}`);
    const buy = form?.querySelector('[name="add"]');
    const sticky = root.querySelector('[data-ew-sticky]');
    const stickyButton = root.querySelector('[data-ew-sticky-add]');
    const dialog = root.querySelector('.ew-size-dialog');
    let dotSlides = [];
    let scheduled = false;
    let frame = null;
    const unsubscribers = [];
    let previewObserver;
    let observedPreview;
    function activeDot() {
      if (!slider || !dots || !dotSlides.length) return;
      const left = slider.scrollLeft;
      let best = 0, distance = Infinity;
      dotSlides.forEach((slide, index) => {
        const d = Math.abs(slide.offsetLeft - dotSlides[0].offsetLeft - left);
        if (d < distance) { distance = d; best = index; }
      });
      [...dots.children].forEach((button, i) => {
        const value = String(i === best);
        if (button.getAttribute('aria-current') !== value) button.setAttribute('aria-current', value);
      });
    }
    function buildDots() {
      if (!slider || !dots) return;
      dotSlides = [...slider.querySelectorAll('li[data-media-id]')];
      dots.replaceChildren();
      dotSlides.forEach((slide, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.target = slide.dataset.mediaId;
        button.setAttribute('aria-label', `${root.dataset.ewMediaLabel} ${index + 1}`);
        button.setAttribute('aria-current', String(index === 0));
        dots.append(button);
      });
      dots.hidden = dotSlides.length < 2;
      activeDot();
    }
    function sync() {
      scheduled = false;
      frame = null;
      if (!root.isConnected) return;
      if (buy && !buy.disabled && buy.getAttribute('aria-disabled') !== 'true') {
        const label = buy.querySelector(':scope > span');
        if (label && label.textContent.trim() !== root.dataset.ewAddLabel) label.textContent = root.dataset.ewAddLabel;
      }
      if (stickyButton && buy) {
        const disabled = buy.disabled || buy.getAttribute('aria-disabled') === 'true';
        if (stickyButton.disabled !== disabled) stickyButton.disabled = disabled;
        const label = buy.querySelector(':scope > span')?.textContent.trim() || root.dataset.ewAddLabel;
        if (stickyButton.textContent !== label) stickyButton.textContent = label;
      }
      const price = root.querySelector('.price--on-sale .price__sale .price-item--sale') || root.querySelector('.price__regular .price-item--regular');
      const stickyPrice = root.querySelector('[data-ew-sticky-price]');
      if (price && stickyPrice && stickyPrice.textContent !== price.textContent.trim()) stickyPrice.textContent = price.textContent.trim();
      const preview = [
        document.getElementById('PBarNextFrameWrapper'),
        document.getElementById('PBarNextFrame'),
        document.getElementById('preview-bar-iframe'),
      ].find((element) => {
        if (!element) return false;
        const r = element.getBoundingClientRect();
        return r.height > 0 && r.height < innerHeight / 3 && r.bottom >= innerHeight - 32;
      });
      const previewRect = preview?.getBoundingClientRect();
      const offset = `${previewRect ? Math.max(0, innerHeight - previewRect.top) : 0}px`;
      if (root.style.getPropertyValue('--ew-preview-offset') !== offset) root.style.setProperty('--ew-preview-offset', offset);
      if (modal && modal.style.getPropertyValue('--ew-preview-offset') !== offset) modal.style.setProperty('--ew-preview-offset', offset);
      if (sticky && buy) {
        const hidden = !(innerWidth < 750 && buy.getBoundingClientRect().bottom < 0);
        if (sticky.hidden !== hidden) sticky.hidden = hidden;
        if (sticky.style.bottom !== offset) sticky.style.bottom = offset;
        if (preview && preview !== observedPreview && typeof ResizeObserver !== 'undefined') {
          previewObserver?.disconnect();
          previewObserver = new ResizeObserver(scheduleSync);
          previewObserver.observe(preview);
          observedPreview = preview;
        }
      }
    }
    function scheduleSync() {
      if (scheduled) return;
      scheduled = true;
      frame = requestAnimationFrame(sync);
    }
    dots?.addEventListener('click', (event) => {
      if (event.defaultPrevented) return;
      const button = event.target.closest('button[data-target]');
      if (button && gallery) gallery.setActiveMedia?.(button.dataset.target);
    }, opts);
    slider?.addEventListener('scroll', activeDot, { ...opts, passive: true });
    viewer?.addEventListener('slideChanged', activeDot, opts);
    root.addEventListener('click', (event) => {
      if (event.defaultPrevented) return;
      if (event.target.closest('[data-ew-size-help]') && dialog) dialog.showModal();
      if (event.target.closest('[data-ew-dialog-close]') && dialog) dialog.close();
      if (event.target.closest('[data-ew-sticky-add]') && form && !stickyButton.disabled) form.requestSubmit(buy);
    }, opts);
    dialog?.addEventListener('click', (event) => {
      if (event.target !== dialog) return;
      const r = dialog.getBoundingClientRect();
      if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) dialog.close();
    }, opts);
    window.addEventListener('scroll', scheduleSync, { ...opts, passive: true });
    window.addEventListener('resize', scheduleSync, opts);
    form?.addEventListener('submit', scheduleSync, opts);
    const observer = new MutationObserver(scheduleSync);
    // The editor mutates product-info classes and DOM for its inspector. Do not
    // observe that entire subtree: doing so can feed changes back into its bridge.
    // Customer pages only need the actual submit button and price container.
    if (!designMode) {
      if (buy) observer.observe(buy, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled'] });
      const priceContainer = document.getElementById(`price-${id}`);
      if (priceContainer && root.contains(priceContainer)) observer.observe(priceContainer, { subtree: true, childList: true, characterData: true });
    }
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      unsubscribers.push(subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        if (event.data.sectionId !== id) return;
        const renderedPrice = event.data.html?.querySelector('[data-ew-sticky-price]');
        const stickyPrice = root.querySelector('[data-ew-sticky-price]');
        if (renderedPrice && stickyPrice) stickyPrice.textContent = renderedPrice.textContent.trim();
        buildDots();
        sync();
      }));
      if (PUB_SUB_EVENTS.cartUpdate) unsubscribers.push(subscribe(PUB_SUB_EVENTS.cartUpdate, scheduleSync));
    }
    const modal = root.querySelector(`[id="ProductModal-${id}"]`) || document.getElementById(`ProductModal-${id}`);
    if (modal && !modal.querySelector('.ew-modal-arrow')) {
      modal.dataset.ewOwner = id;
      modal.classList.add('ew-lightbox');
      const navigate = (direction) => {
        const content = modal.querySelector('.product-media-modal__content');
        const items = [...(content?.children || [])].filter((item) => item.hasAttribute('data-media-id'));
        if (!items.length) return;
        const current = Math.max(0, items.findIndex((item) => item.classList.contains('active')));
        const next = items[(current + direction + items.length) % items.length];
        const opener = root.querySelector(`.product__media-toggle[data-media-id="${next.dataset.mediaId}"]`);
        if (opener && modal.showActiveMedia) { modal.openedBy = opener; modal.showActiveMedia(); }
      };
      [-1, 1].forEach((direction) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `ew-modal-arrow ew-modal-arrow--${direction === -1 ? 'previous' : 'next'}`;
        button.setAttribute('aria-label', direction === -1 ? root.dataset.ewPreviousLabel : root.dataset.ewNextLabel);
        button.textContent = direction === -1 ? '‹' : '›';
        // Dawn closes media modals on bubbled mouse pointerup. Navigation
        // controls must not be interpreted as a backdrop click.
        button.addEventListener('pointerup', (event) => event.stopPropagation(), opts);
        button.addEventListener('click', () => navigate(direction), opts);
        modal.append(button);
      });
      modal.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') navigate(-1);
        if (event.key === 'ArrowRight') navigate(1);
      }, opts);
    }
    buildDots();
    sync();
    instances.set(root, {
      abort, observer, modal, dialog,
      unsubscribe: () => unsubscribers.forEach((unsubscribe) => unsubscribe()),
      cleanupPreview: () => previewObserver?.disconnect(),
      cancelFrame: () => { if (frame !== null) cancelAnimationFrame(frame); },
    });
  }
  function scan(container = document) {
    for (const [root, instance] of instances) {
      if (!root.isConnected) destroy(root, instance);
    }
    if (container.matches?.('.ew-product')) init(container);
    container.querySelectorAll?.('.ew-product').forEach(init);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
  else scan();
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    for (const [root, instance] of instances) {
      const sectionId = event.detail?.sectionId;
      if (root.isConnected && (sectionId ? sectionId !== root.dataset.ewSection : !event.target.contains(root))) continue;
      destroy(root, instance);
    }
  });
  const closeEditorDialogs = () => {
    if (!window.Shopify?.designMode) return;
    for (const instance of instances.values()) {
      if (instance.dialog?.open) instance.dialog.close();
    }
  };
  document.addEventListener('shopify:section:select', closeEditorDialogs);
  document.addEventListener('shopify:block:select', closeEditorDialogs);
})();
